import { memo, useMemo } from 'react';
import {
  Card,
  Stack,
  Text,
  Select,
  Flex,
  Image,
  Group,
  SimpleGrid,
  Box,
} from '@mantine/core';
import type { Device } from '../../types';
import { getDeviceImageSrc } from '../../utils/deviceImage';

type SelectedDeviceData =
  | {
      value: string;
      label: string;
      device: Device;
    }
  | undefined;

interface SelectionCardProps {
  title: string;
  description: string;
  label: string;
  placeholder: string;
  data: Array<{ value: string; label: string }>;
  value: string | null;
  onChange: (value: string | null) => void;
  loading?: boolean;
  error?: string | null;
  searchable?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  isActive?: boolean;
  showDeviceInfo?: boolean;
  selectedDeviceData?: SelectedDeviceData;
  allDevices?: Device[];
  rightAction?: React.ReactNode;
}

const SelectionCard = memo<SelectionCardProps>(
  ({
    title,
    description,
    label,
    placeholder,
    data,
    value,
    onChange,
    loading = false,
    error = null,
    searchable = true,
    clearable = true,
    disabled = false,
    isActive = true,
    showDeviceInfo = false,
    selectedDeviceData,
    allDevices = [],
    rightAction,
  }) => {
    // Trovare i sensori collegati al device selezionato (quando è un VR device)
    const connectedSensors = useMemo(() => {
      if (!selectedDeviceData?.device || !allDevices.length) {
        return [];
      }

      const device = selectedDeviceData.device;
      const isVrHeadset = device.type === 'vr-headset';

      if (!isVrHeadset) {
        return [];
      }

      // Cercare i sensori in allDevices che hanno connected_to uguale all'id del device selezionato
      const sensors = allDevices.filter(
        d =>
          d.type === 'smartwatch' &&
          (d.connected_to === device.id ||
            d.connected_to === device.clientId ||
            d.connected_to === device.code ||
            String(d.connected_to) === String(device.id))
      );

      return sensors;
    }, [selectedDeviceData, allDevices]);

    // Trovare il device collegato tramite connected_to (quando il device selezionato è un sensore)
    const connectedDevice = useMemo(() => {
      const device = selectedDeviceData?.device;
      const isVrHeadset = device?.type === 'vr-headset';

      // Se è un VR headset, non cerchiamo un device collegato (i sensori sono gestiti da connectedSensors)
      if (isVrHeadset || !device?.connected_to || !allDevices.length) {
        return null;
      }

      const connectedTo = device.connected_to;
      const found = allDevices.find(
        d =>
          d.id === connectedTo ||
          d.clientId === connectedTo ||
          d.code === connectedTo ||
          String(d.id) === String(connectedTo)
      );

      return found || null;
    }, [selectedDeviceData, allDevices]);
    return (
      <Card
        withBorder
        p='md'
        style={{
          opacity: isActive ? 1 : 0.8,
        }}
      >
        <Stack gap='md'>
          <Text fw={500} size='lg'>
            {title}
          </Text>
          <Text size='sm' c='dimmed'>
            {description}
          </Text>

          {rightAction ? (
            <Group justify='space-between' align='flex-end' gap='xs'>
              <Select
                label={label}
                placeholder={loading ? 'Caricamento...' : placeholder}
                data={data}
                value={value}
                onChange={onChange}
                searchable={searchable}
                clearable={clearable}
                disabled={disabled || loading}
                error={error}
                style={{ flex: 1 }}
              />
              <Box style={{ paddingBottom: '2px' }}>{rightAction}</Box>
            </Group>
          ) : (
            <Select
              label={label}
              placeholder={loading ? 'Caricamento...' : placeholder}
              data={data}
              value={value}
              onChange={onChange}
              searchable={searchable}
              clearable={clearable}
              disabled={disabled || loading}
              error={error}
            />
          )}

          {/* Informazioni Dispositivo - Solo per dispositivi */}
          {showDeviceInfo && selectedDeviceData && (
            <Card withBorder p='sm' bg='gray.0'>
              <Flex direction={{ base: 'column', md: 'row' }} gap='md'>
                {/* VR Device Card */}
                <Flex align='center' gap='md' wrap='nowrap' flex={1}>
                  <Group align='center' w='100%'>
                    <Flex
                      justify='center'
                      align='center'
                      style={{ overflow: 'hidden' }}
                      h={85}
                      w={85}
                    >
                      <Image
                        src={
                          selectedDeviceData.device
                            ? getDeviceImageSrc(selectedDeviceData.device)
                            : '/meta-quest-3.webp'
                        }
                        h={100}
                        w={100}
                        alt='VR Headset'
                      />
                    </Flex>
                    <Stack gap='sm' flex={1} w='100%'>
                      <Stack
                        gap={2}
                        bg='white'
                        p='sm'
                        style={{ borderRadius: 5 }}
                        w='100%'
                      >
                        <Group gap={5} w='100%'>
                          <Text c='dimmed' size='sm'>
                            Nome:
                          </Text>
                          <Text fw={700} size='sm'>
                            {selectedDeviceData.device.name || 'N/A'}
                          </Text>
                        </Group>
                        <Group gap={5} w='100%'>
                          <Text c='dimmed' size='sm'>
                            Modello:
                          </Text>
                          <Text fw={700} size='sm'>
                            {selectedDeviceData.device.model || 'N/A'}
                          </Text>
                        </Group>
                        <Group gap={5} w='100%'>
                          <Text c='dimmed' size='sm'>
                            Device Code:
                          </Text>
                          <Text fw={700} size='sm'>
                            {selectedDeviceData.device.code ||
                              selectedDeviceData.device.clientId ||
                              'N/A'}
                          </Text>
                        </Group>
                      </Stack>
                    </Stack>
                  </Group>
                </Flex>

                {/* Sensori Collegati */}
                {connectedSensors.length > 0 && (
                  <SimpleGrid
                    cols={{ base: 1, md: connectedSensors.length }}
                    spacing='md'
                    flex={1}
                  >
                    {connectedSensors.map((sensor: Device, index: number) => {
                      const isPolarDevice =
                        sensor?.model?.toLowerCase().includes('polar') ?? false;
                      const sensorImageSrc = isPolarDevice
                        ? '/polar_360.webp'
                        : '/galaxy.avif';

                      return (
                        <Flex
                          key={index}
                          align='center'
                          gap='md'
                          wrap='nowrap'
                          w='100%'
                        >
                          <Group align='center' w='100%'>
                            <Flex
                              justify='center'
                              align='center'
                              style={{ overflow: 'hidden' }}
                              h={85}
                              w={85}
                            >
                              <Image
                                src={sensorImageSrc}
                                h={100}
                                w={100}
                                alt='Heart Rate Sensor'
                              />
                            </Flex>
                            <Stack gap='sm' flex={1} w='100%'>
                              <Stack
                                gap={2}
                                bg='white'
                                p='sm'
                                style={{ borderRadius: 5 }}
                                w='100%'
                              >
                                <Group gap={5} w='100%'>
                                  <Text c='dimmed' size='sm'>
                                    Nome:
                                  </Text>
                                  <Text fw={700} size='sm'>
                                    {sensor.name ||
                                      sensor.clientId ||
                                      `Sensore ${index + 1}`}
                                  </Text>
                                </Group>
                                <Group gap={5} w='100%'>
                                  <Text c='dimmed' size='sm'>
                                    Modello:
                                  </Text>
                                  <Text fw={700} size='sm'>
                                    {sensor.model || 'N/A'}
                                  </Text>
                                </Group>
                                <Group gap={5} w='100%'>
                                  <Text c='dimmed' size='sm'>
                                    Device Code:
                                  </Text>
                                  <Text fw={700} size='sm'>
                                    {sensor.code || sensor.clientId || 'N/A'}
                                  </Text>
                                </Group>
                              </Stack>
                            </Stack>
                          </Group>
                        </Flex>
                      );
                    })}
                  </SimpleGrid>
                )}

                {/* Device collegato tramite connected_to */}
                {connectedDevice && (
                  <Flex align='center' gap='md' wrap='nowrap' w='100%'>
                    <Group align='center' w='100%'>
                      <Flex
                        justify='center'
                        align='center'
                        style={{ overflow: 'hidden' }}
                        h={85}
                        w={85}
                      >
                        <Image
                          src={getDeviceImageSrc(connectedDevice)}
                          h={100}
                          w={100}
                          alt={
                            connectedDevice.type === 'vr-headset'
                              ? 'VR Headset'
                              : 'Heart Rate Sensor'
                          }
                        />
                      </Flex>
                      <Stack gap='sm' flex={1} w='100%'>
                        <Stack
                          gap={2}
                          bg='white'
                          p='sm'
                          style={{ borderRadius: 5 }}
                          w='100%'
                        >
                          <Group gap={5} w='100%'>
                            <Text c='dimmed' size='sm'>
                              Nome:
                            </Text>
                            <Text fw={700} size='sm'>
                              {connectedDevice.name || 'N/A'}
                            </Text>
                          </Group>
                          <Group gap={5} w='100%'>
                            <Text c='dimmed' size='sm'>
                              Modello:
                            </Text>
                            <Text fw={700} size='sm'>
                              {connectedDevice.model || 'N/A'}
                            </Text>
                          </Group>
                          <Group gap={5} w='100%'>
                            <Text c='dimmed' size='sm'>
                              Device Code:
                            </Text>
                            <Text fw={700} size='sm'>
                              {connectedDevice.code ||
                                connectedDevice.clientId ||
                                'N/A'}
                            </Text>
                          </Group>
                          {connectedDevice.type && (
                            <Group gap={5} w='100%'>
                              <Text c='dimmed' size='sm'>
                                Tipo:
                              </Text>
                              <Text fw={700} size='sm'>
                                {connectedDevice.type}
                              </Text>
                            </Group>
                          )}
                        </Stack>
                      </Stack>
                    </Group>
                  </Flex>
                )}
              </Flex>
            </Card>
          )}
        </Stack>
      </Card>
    );
  }
);

SelectionCard.displayName = 'SelectionCard';

export default SelectionCard;
